// src/components/financeOfficer/ProjectFinancialCard.jsx
import React from "react";
import { FiDollarSign, FiTrendingUp, FiActivity, FiCheckCircle, FiUsers, FiPackage, FiArrowUpRight } from "react-icons/fi";

export default function ProjectFinancialCard({ data }) {
  if (!data) return null;

  const utilizationColor = data.metrics.budgetUtilization > 90 ? "text-red-600" : data.metrics.budgetUtilization > 70 ? "text-yellow-600" : "text-green-600";

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6 border-l-4 border-blue-500 animate-fade-in">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-800">{data.project.title}</h2>
          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded uppercase font-semibold">
            {data.project.status}
          </span>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-500 uppercase">Budget Utilization</p>
          <p className={`text-2xl font-bold ${utilizationColor}`}>
            {data.metrics.budgetUtilization}%
          </p>
        </div>
      </div>

      {/* UPDATED: Grid is now 2 columns to accommodate the new 'Received' block */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        
        {/* 1. Target Budget (The Plan) */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
          <div className="flex items-center gap-2 text-gray-600 text-xs uppercase font-bold mb-1">
            <FiDollarSign /> Project Budget
          </div>
          <p className="text-xl font-bold text-gray-800">
            ${Number(data.budget.total).toLocaleString()}
          </p>
          <p className="text-[10px] text-gray-400 mt-1">Planned Amount</p>
        </div>

        {/* 2. NEW: Funds Received (Actual Inflow) */}
        <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
          <div className="flex items-center gap-2 text-indigo-700 text-xs uppercase font-bold mb-1">
            <FiArrowUpRight /> Funds Received
          </div>
          <p className="text-xl font-bold text-indigo-900">
            ${Number(data.budget.allocated).toLocaleString()}
          </p>
          <p className="text-[10px] text-indigo-600 mt-1">Actual Income / Donations</p>
        </div>

        {/* 3. Committed Spend */}
        <div className="bg-orange-50 p-4 rounded-lg border border-orange-100">
          <div className="flex items-center gap-2 text-orange-700 text-xs uppercase font-bold mb-1">
            <FiTrendingUp /> Committed Spend
          </div>
          <p className="text-xl font-bold text-orange-900">
            ${Number(data.spend.totalCommitted).toLocaleString()}
          </p>
           <div className="w-full bg-orange-200 rounded-full h-1.5 mt-2">
            <div 
              className="bg-orange-600 h-1.5 rounded-full transition-all duration-1000" 
              style={{ width: `${Math.min(data.metrics.budgetUtilization, 100)}%` }}
            ></div>
          </div>
        </div>

        {/* 4. Remaining Cash Balance (Actual) */}
        <div className="bg-green-50 p-4 rounded-lg border border-green-100">
          <div className="flex items-center gap-2 text-green-700 text-xs uppercase font-bold mb-1">
            <FiCheckCircle /> Cash Balance
          </div>
          <p className="text-xl font-bold text-green-900">
            ${Number(data.budget.remaining).toLocaleString()}
          </p>
          <p className="text-xs text-green-700 mt-1">
            {data.budget.remaining < 0 ? "Deficit (Overspent Income)" : "Surplus (Unspent Income)"}
          </p>
        </div>

      </div>

      {/* Breakdown */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Spend Breakdown</h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center text-sm">
          <div className="p-2 bg-white border rounded shadow-sm">
            <div className="text-gray-500 text-xs">Procurement</div>
            <div className="font-bold text-gray-800">${Number(data.spend.breakdown.procurement).toLocaleString()}</div>
          </div>
          <div className="p-2 bg-white border rounded shadow-sm">
            <div className="text-gray-500 text-xs">Travel</div>
            <div className="font-bold text-gray-800">${Number(data.spend.breakdown.travel).toLocaleString()}</div>
          </div>
          <div className="p-2 bg-white border rounded shadow-sm">
            <div className="text-gray-500 text-xs">Assets</div>
            <div className="font-bold text-gray-800">${Number(data.spend.breakdown.assets).toLocaleString()}</div>
          </div>
          <div className="p-2 bg-white border rounded shadow-sm">
            <div className="text-gray-500 text-xs flex items-center justify-center gap-1">
              <FiUsers className="w-3 h-3" /> Payroll
            </div>
            <div className="font-bold text-gray-800">${Number(data.spend.breakdown.payroll).toLocaleString()}</div>
          </div>
          {/* Items Added */}
          <div className="p-2 bg-white border rounded shadow-sm">
            <div className="text-gray-500 text-xs flex items-center justify-center gap-1">
              <FiPackage className="w-3 h-3" /> Items
            </div>
            <div className="font-bold text-gray-800">${Number(data.spend.breakdown.items).toLocaleString()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}